import { BonificoOrderStatus, CalendarDate } from "./BonificoOrderDTO.ts";
import {
  hasActiveListFilters,
  SortDirection,
} from "../../UI/Table/listUrlParams.ts";

export type { SortDirection };

export const B2B_ORDERS_PRODUCT_FILTER = "b2b";

export type BonificoOrderSortField =
  | "createdAt"
  | "customer"
  | "amount"
  | "receiptDate"
  | "valueDate";

export type BonificoOrdersFilters = {
  statuses?: BonificoOrderStatus[];
  productIds?: string[];
  createdFrom?: CalendarDate;
  createdTo?: CalendarDate;
  amountMinInCents?: number;
  amountMaxInCents?: number;
  receiptFrom?: CalendarDate;
  receiptTo?: CalendarDate;
  receiptMissing?: boolean;
  valueFrom?: CalendarDate;
  valueTo?: CalendarDate;
  valueMissing?: boolean;
};

export type BonificoOrdersListQuery = BonificoOrdersFilters & {
  search?: string;
  sortBy: BonificoOrderSortField;
  sortDirection: SortDirection;
  page: number;
  pageSize: number;
};

export const DEFAULT_BONIFICO_ORDERS_SORT = {
  sortBy: "createdAt",
  sortDirection: "desc",
} as const;

export const BONIFICO_ORDERS_PAGE_SIZE = 50;

const MAX_BONIFICO_ORDERS_PAGE_SIZE = 100;

export function awaitingInvoiceOrdersQuery(
  valueTo?: CalendarDate,
): BonificoOrdersListQuery {
  return {
    statuses: [BonificoOrderStatus.AwaitingInvoice],
    valueTo,
    sortBy: "valueDate",
    sortDirection: "asc",
    page: 0,
    pageSize: MAX_BONIFICO_ORDERS_PAGE_SIZE,
  };
}

export function toBonificoOrdersApiParams(
  query: BonificoOrdersListQuery,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean | undefined> = {
    search: query.search || undefined,
    status: query.statuses?.length ? query.statuses.join(",") : undefined,
    product: query.productIds?.length ? query.productIds.join(",") : undefined,
    createdFrom: query.createdFrom,
    createdTo: query.createdTo,
    amountMin: query.amountMinInCents,
    amountMax: query.amountMaxInCents,
    receiptFrom: query.receiptFrom,
    receiptTo: query.receiptTo,
    receiptMissing: query.receiptMissing || undefined,
    valueFrom: query.valueFrom,
    valueTo: query.valueTo,
    valueMissing: query.valueMissing || undefined,
    sortBy: query.sortBy,
    sortDirection: query.sortDirection,
    page: query.page,
    pageSize: query.pageSize,
  };
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  ) as Record<string, string | number | boolean>;
}

const PAGINATION_PARAMS = ["page", "pageSize"];

export function toBonificoOrdersExportApiParams(
  query: BonificoOrdersListQuery,
): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(toBonificoOrdersApiParams(query)).filter(
      ([name]) => !PAGINATION_PARAMS.includes(name),
    ),
  );
}

export function pickBonificoOrdersFilters(
  query: BonificoOrdersListQuery,
): BonificoOrdersFilters {
  return {
    statuses: query.statuses,
    productIds: query.productIds,
    createdFrom: query.createdFrom,
    createdTo: query.createdTo,
    amountMinInCents: query.amountMinInCents,
    amountMaxInCents: query.amountMaxInCents,
    receiptFrom: query.receiptFrom,
    receiptTo: query.receiptTo,
    receiptMissing: query.receiptMissing,
    valueFrom: query.valueFrom,
    valueTo: query.valueTo,
    valueMissing: query.valueMissing,
  };
}

export function hasActiveBonificoOrdersFilters(
  filters: BonificoOrdersFilters,
): boolean {
  return hasActiveListFilters(filters);
}
